import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Activity, Users, Heart, AlertTriangle, Bell, RefreshCw, Loader2, CheckCircle2,
  Clock, ChevronRight, Brain, Sparkles, Calendar, MessageSquare, Phone, Send,
  UserCheck, ClipboardList, Flag, Home, Thermometer, TrendingDown
} from "lucide-react";

const HOTLIST_CATEGORY = {
  imminent: { label: "Imminent (≤3 days)", color: "bg-red-600 text-white" },
  critical: { label: "Critical (4-7 days)", color: "bg-orange-500 text-white" },
  short_term: { label: "Short-term (8-14 days)", color: "bg-yellow-500 text-white" }
};

export default function RNAIDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Summary
  const [summary, setSummary] = useState(null);
  const [aiInsights, setAiInsights] = useState("");
  const [insightsLoading, setInsightsLoading] = useState(false);
  
  // Hotlist
  const [hotlist, setHotlist] = useState([]);
  const [hotlistCandidates, setHotlistCandidates] = useState([]);
  
  // Care Plans
  const [carePlanRecs, setCarePlanRecs] = useState([]);
  
  // Handoffs
  const [handoffs, setHandoffs] = useState([]);
  
  // IDG
  const [idgReviews, setIdgReviews] = useState([]);
  
  // Recert Visits
  const [recertVisits, setRecertVisits] = useState([]);
  
  // Dialogs
  const [showAddHotlist, setShowAddHotlist] = useState(false);
  const [showFamilyMessage, setShowFamilyMessage] = useState(false);
  const [newHotlist, setNewHotlist] = useState({ patient_id: "", patient_name: "", prognosis_days: 7, category: "short_term", primary_diagnosis: "", current_symptoms: [] });
  const [familyMessage, setFamilyMessage] = useState({ patient_id: "", patient_name: "", subject: "", message: "", priority: "normal" });

  const allowedRoles = ["nurse", "rn", "lpn", "staff", "coordinator", "admin", "administrator"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      toast.error("Access denied - RN access required");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSummary(),
        loadHotlist(),
        loadCarePlanRecs(),
        loadHandoffs(),
        loadIdgReviews(),
        loadRecertVisits()
      ]);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await api.get("/rn-dashboard/summary");
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Summary error:", error);
    }
  };

  const loadHotlist = async () => {
    try {
      const response = await api.get("/rn-dashboard/hotlist");
      setHotlist(response.data.patients || []);
    } catch (error) {
      setHotlist([]);
    }
  };

  const loadCarePlanRecs = async () => {
    try {
      const response = await api.get("/rn-dashboard/care-plans/pending");
      setCarePlanRecs(response.data.recommendations || []);
    } catch (error) {
      setCarePlanRecs([]);
    }
  };

  const loadHandoffs = async () => {
    try {
      const response = await api.get("/rn-dashboard/handoff-notifications?unread_only=true");
      setHandoffs(response.data.notifications || []);
    } catch (error) {
      setHandoffs([]);
    }
  };

  const loadIdgReviews = async () => {
    try {
      const response = await api.get("/rn-dashboard/idg/upcoming");
      setIdgReviews(response.data.reviews || []);
    } catch (error) {
      setIdgReviews([]);
    }
  };

  const loadRecertVisits = async () => {
    try {
      const response = await api.get("/rn-dashboard/recert-visits/upcoming");
      setRecertVisits(response.data.visits || []);
    } catch (error) {
      setRecertVisits([]);
    }
  };

  const loadAiInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await api.get("/rn-dashboard/ai-insights");
      setAiInsights(response.data.insights || "");
    } catch (error) {
      toast.error("Failed to load AI insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleAddToHotlist = async () => {
    if (!newHotlist.patient_name) {
      toast.error("Patient name required");
      return;
    }
    try {
      await api.post("/rn-dashboard/hotlist", newHotlist);
      toast.success("Patient added to hotlist");
      setShowAddHotlist(false);
      setNewHotlist({ patient_id: "", patient_name: "", prognosis_days: 7, category: "short_term", primary_diagnosis: "", current_symptoms: [] });
      loadHotlist();
      loadSummary();
    } catch (error) {
      toast.error("Failed to add to hotlist");
    }
  };

  const handleAcceptRecommendation = async (recId) => {
    try {
      await api.put(`/rn-dashboard/care-plans/recommendations/${recId}/accept`);
      toast.success("Recommendation accepted");
      loadCarePlanRecs();
    } catch (error) {
      toast.error("Failed to accept");
    }
  };

  const handleMarkHandoffRead = async (notifId) => {
    try {
      await api.put(`/rn-dashboard/handoff-notifications/${notifId}/read`);
      loadHandoffs();
      loadSummary();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleSendFamilyMessage = async () => {
    if (!familyMessage.subject || !familyMessage.message) {
      toast.error("Subject and message required");
      return;
    }
    try {
      await api.post("/rn-dashboard/family-messages", familyMessage);
      toast.success("Message sent to family");
      setShowFamilyMessage(false);
      setFamilyMessage({ patient_id: "", patient_name: "", subject: "", message: "", priority: "normal" });
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="rn-dashboard-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-400 mb-4" />
          <p className="text-slate-400">Loading RN Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="rn-ai-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Heart className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">RN AI Dashboard</h1>
                <p className="text-emerald-200">Care Plans, Hotlist, IDG, F2F & Recertification</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={loadData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-2" />Refresh
              </Button>
              <Button onClick={loadAiInsights} className="bg-white text-emerald-700 hover:bg-emerald-50" disabled={insightsLoading} data-testid="rn-ai-insights-btn">
                {insightsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                AI Insights
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* AI Insights Banner */}
        {aiInsights && (
          <Card className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-emerald-500/30 mb-6" data-testid="rn-ai-insights-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Brain className="h-5 w-5 text-emerald-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-emerald-300 mb-1">AI-Generated Shift Priorities</p>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{aiInsights}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 mx-auto text-blue-400 mb-2" />
                <p className="text-2xl font-bold text-white">{summary.assigned_patients || 0}</p>
                <p className="text-xs text-slate-400">Assigned Patients</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <Flag className="h-6 w-6 mx-auto text-red-400 mb-2" />
                <p className="text-2xl font-bold text-white">{summary.hotlist?.total || 0}</p>
                <p className="text-xs text-slate-400">On Hotlist</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <TrendingDown className="h-6 w-6 mx-auto text-orange-400 mb-2" />
                <p className="text-2xl font-bold text-white">{summary.hotlist?.imminent || 0}</p>
                <p className="text-xs text-slate-400">Imminent</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <Bell className="h-6 w-6 mx-auto text-yellow-400 mb-2" />
                <p className="text-2xl font-bold text-white">{summary.unread_handoffs || 0}</p>
                <p className="text-xs text-slate-400">Handoff Alerts</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 mx-auto text-cyan-400 mb-2" />
                <p className="text-2xl font-bold text-white">{summary.recerts_at_risk || 0}</p>
                <p className="text-xs text-slate-400">Recerts Due</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600 text-white">
              <Activity className="h-4 w-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="hotlist" className="data-[state=active]:bg-emerald-600 text-white">
              <Flag className="h-4 w-4 mr-2" />Hotlist
              {hotlist.filter(h => h.category === "imminent").length > 0 && (
                <Badge className="ml-2 bg-red-500">{hotlist.filter(h => h.category === "imminent").length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="careplans" className="data-[state=active]:bg-emerald-600 text-white">
              <ClipboardList className="h-4 w-4 mr-2" />Care Plans
            </TabsTrigger>
            <TabsTrigger value="handoffs" className="data-[state=active]:bg-emerald-600 text-white">
              <UserCheck className="h-4 w-4 mr-2" />Handoffs
              {handoffs.length > 0 && <Badge className="ml-2 bg-yellow-500">{handoffs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="idg" className="data-[state=active]:bg-emerald-600 text-white">
              <Users className="h-4 w-4 mr-2" />IDG
            </TabsTrigger>
            <TabsTrigger value="recert" className="data-[state=active]:bg-emerald-600 text-white">
              <Calendar className="h-4 w-4 mr-2" />Recert/F2F
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Hotlist Preview */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Flag className="h-5 w-5 text-red-400" />
                      Hotlist Patients
                    </CardTitle>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddHotlist(true)}>
                      Add Patient
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    <div className="space-y-2">
                      {hotlist.slice(0, 5).map((patient) => (
                        <div key={patient.id} className={`p-3 rounded-lg ${patient.category === "imminent" ? "bg-red-500/20 border border-red-500/30" : "bg-slate-700/50"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">{patient.patient_name}</p>
                              <p className="text-sm text-slate-400">{patient.primary_diagnosis}</p>
                            </div>
                            <Badge className={HOTLIST_CATEGORY[patient.category]?.color}>{patient.prognosis_days}d</Badge>
                          </div>
                        </div>
                      ))}
                      {hotlist.length === 0 && (
                        <div className="text-center py-8 text-slate-400">
                          <Flag className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>No patients on hotlist</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Handoff Notifications */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Bell className="h-5 w-5 text-yellow-400" />
                    Handoff Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    <div className="space-y-2">
                      {handoffs.slice(0, 5).map((notif) => (
                        <div key={notif.id} className={`p-3 rounded-lg ${notif.has_significant_changes ? "bg-orange-500/20 border border-orange-500/30" : "bg-slate-700/50"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">Patient seen by {notif.visiting_rn_name}</p>
                              <p className="text-sm text-slate-400">{notif.has_significant_changes ? "Changes noted - review required" : "No significant changes"}</p>
                            </div>
                            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handleMarkHandoffRead(notif.id)}>
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {handoffs.length === 0 && (
                        <div className="text-center py-8 text-slate-400">
                          <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-50 text-green-400" />
                          <p>No pending handoffs</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Hotlist Tab */}
          <TabsContent value="hotlist" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">End-of-Life Hotlist</CardTitle>
                  <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddHotlist(true)} data-testid="add-hotlist-btn">
                    Add to Hotlist
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {hotlist.map((patient) => (
                      <div key={patient.id} className={`p-4 rounded-lg ${patient.category === "imminent" ? "bg-red-500/20 border border-red-500/30" : patient.category === "critical" ? "bg-orange-500/10 border border-orange-500/30" : "bg-slate-700/50"}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-white">{patient.patient_name}</p>
                              <Badge className={HOTLIST_CATEGORY[patient.category]?.color}>{HOTLIST_CATEGORY[patient.category]?.label}</Badge>
                            </div>
                            <p className="text-sm text-slate-400">{patient.primary_diagnosis}</p>
                            <p className="text-xs text-slate-500">Est. {patient.prognosis_days} days | Last visit: {patient.last_visit ? new Date(patient.last_visit).toLocaleDateString() : "N/A"}</p>
                            {patient.ai_insights && (
                              <div className="mt-2 p-2 bg-emerald-500/10 rounded text-sm text-emerald-300">
                                <Brain className="h-3 w-3 inline mr-1" />{patient.ai_insights.slice(0, 150)}...
                              </div>
                            )}
                          </div>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => { setFamilyMessage({ ...familyMessage, patient_id: patient.patient_id, patient_name: patient.patient_name }); setShowFamilyMessage(true); }}>
                            <MessageSquare className="h-4 w-4 mr-1" />Family
                          </Button>
                        </div>
                      </div>
                    ))}
                    {hotlist.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <Flag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No patients on hotlist</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Care Plans Tab */}
          <TabsContent value="careplans" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="h-5 w-5 text-emerald-400" />
                  AI Care Plan Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {carePlanRecs.map((rec) => (
                      <div key={rec.id} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-white">{rec.patient_name}</p>
                              <Badge className={rec.priority === "high" ? "bg-orange-500" : "bg-blue-500"}>{rec.priority}</Badge>
                            </div>
                            <p className="text-white font-medium mt-1">{rec.title}</p>
                            <p className="text-sm text-slate-400">{rec.description}</p>
                            {rec.interventions?.length > 0 && (
                              <div className="mt-2 text-sm text-slate-300">
                                <p className="text-xs text-slate-500 mb-1">Interventions:</p>
                                <ul className="list-disc list-inside">
                                  {rec.interventions.slice(0, 3).map((int, idx) => (
                                    <li key={idx}>{int}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAcceptRecommendation(rec.id)}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Accept
                          </Button>
                        </div>
                      </div>
                    ))}
                    {carePlanRecs.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No pending recommendations</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Handoffs Tab */}
          <TabsContent value="handoffs" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Patient Handoff Notifications</CardTitle>
                <CardDescription className="text-slate-400">Alerts when another RN sees your patient</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {handoffs.map((notif) => (
                      <div key={notif.id} className={`p-4 rounded-lg ${notif.has_significant_changes ? "bg-orange-500/20 border border-orange-500/30" : "bg-slate-700/50"}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">Patient seen by {notif.visiting_rn_name}</p>
                            <p className="text-sm text-slate-400">{new Date(notif.visit_date).toLocaleString()}</p>
                            {notif.has_significant_changes && (
                              <Badge className="mt-1 bg-orange-500">Significant Changes</Badge>
                            )}
                            {notif.change_summary && (
                              <p className="text-sm text-slate-300 mt-2">{notif.change_summary}</p>
                            )}
                          </div>
                          <Button size="sm" variant="outline" className="border-green-500/50 text-green-400" onClick={() => handleMarkHandoffRead(notif.id)}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Acknowledge
                          </Button>
                        </div>
                      </div>
                    ))}
                    {handoffs.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50 text-green-400" />
                        <p>All handoffs acknowledged</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IDG Tab */}
          <TabsContent value="idg" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">IDG Meeting Preparation</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {idgReviews.map((review) => (
                      <div key={review.id} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{review.patient_name}</p>
                            <p className="text-sm text-slate-400">IDG: {review.idg_meeting_date ? new Date(review.idg_meeting_date).toLocaleDateString() : "TBD"}</p>
                            <Badge className={review.status === "pending" ? "bg-yellow-500" : "bg-green-500"}>{review.status}</Badge>
                          </div>
                          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                            Prepare Summary
                          </Button>
                        </div>
                      </div>
                    ))}
                    {idgReviews.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No upcoming IDG reviews</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recert/F2F Tab */}
          <TabsContent value="recert" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Recertification & F2F Planning</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {recertVisits.map((visit) => (
                      <div key={visit.id} className={`p-4 rounded-lg ${visit.compliance_status === "at_risk" ? "bg-orange-500/10 border border-orange-500/30" : visit.compliance_status === "overdue" ? "bg-red-500/10 border border-red-500/30" : "bg-slate-700/50"}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{visit.patient_name}</p>
                            <p className="text-sm text-slate-400">BP ends: {visit.benefit_period_end ? new Date(visit.benefit_period_end).toLocaleDateString() : "N/A"}</p>
                            {visit.recommended_visit_window && (
                              <p className="text-xs text-slate-500">
                                Visit window: {new Date(visit.recommended_visit_window.start).toLocaleDateString()} - {new Date(visit.recommended_visit_window.end).toLocaleDateString()}
                              </p>
                            )}
                            <div className="flex gap-2 mt-1">
                              <Badge className={visit.compliance_status === "overdue" ? "bg-red-500" : visit.compliance_status === "at_risk" ? "bg-orange-500" : "bg-green-500"}>
                                {visit.compliance_status}
                              </Badge>
                              {visit.f2f_required && !visit.f2f_scheduled && (
                                <Badge variant="outline" className="border-cyan-500/50 text-cyan-400">F2F Needed</Badge>
                              )}
                            </div>
                          </div>
                          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                            <Calendar className="h-4 w-4 mr-1" />Schedule
                          </Button>
                        </div>
                      </div>
                    ))}
                    {recertVisits.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No upcoming recert visits</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Hotlist Dialog */}
      <Dialog open={showAddHotlist} onOpenChange={setShowAddHotlist}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Patient to Hotlist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Patient Name *</Label>
              <Input value={newHotlist.patient_name} onChange={(e) => setNewHotlist({ ...newHotlist, patient_name: e.target.value })} className="bg-slate-700 border-slate-600" data-testid="hotlist-patient-name" />
            </div>
            <div>
              <Label>Primary Diagnosis</Label>
              <Input value={newHotlist.primary_diagnosis} onChange={(e) => setNewHotlist({ ...newHotlist, primary_diagnosis: e.target.value })} className="bg-slate-700 border-slate-600" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prognosis (days)</Label>
                <Input type="number" value={newHotlist.prognosis_days} onChange={(e) => setNewHotlist({ ...newHotlist, prognosis_days: parseInt(e.target.value) || 7 })} className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newHotlist.category} onValueChange={(v) => setNewHotlist({ ...newHotlist, category: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imminent">Imminent (≤3 days)</SelectItem>
                    <SelectItem value="critical">Critical (4-7 days)</SelectItem>
                    <SelectItem value="short_term">Short-term (8-14 days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowAddHotlist(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleAddToHotlist} data-testid="confirm-add-hotlist">Add to Hotlist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family Message Dialog */}
      <Dialog open={showFamilyMessage} onOpenChange={setShowFamilyMessage}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Send Message to Family Portal</DialogTitle>
            {familyMessage.patient_name && <DialogDescription className="text-slate-400">Patient: {familyMessage.patient_name}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input value={familyMessage.subject} onChange={(e) => setFamilyMessage({ ...familyMessage, subject: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="Update on patient condition" />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={familyMessage.priority} onValueChange={(v) => setFamilyMessage({ ...familyMessage, priority: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={familyMessage.message} onChange={(e) => setFamilyMessage({ ...familyMessage, message: e.target.value })} className="bg-slate-700 border-slate-600 min-h-[120px]" placeholder="Your message to the family..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowFamilyMessage(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSendFamilyMessage}>
              <Send className="h-4 w-4 mr-2" />Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
